import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

const AnsweringService = () => {
  return (
    <>
      <Helmet>
        <title>Answering Service - ZiExter | Secrétariat téléphonique virtuel 24/7</title>
        <meta name="description" content="Service de réception d'appels 24/7. Permanence téléphonique virtuelle, filtrage et transfert d'appels professionnels pour PME et indépendants." />
        <link rel="canonical" href="https://ziexter.com/services/answering-service" />
      </Helmet>

      <section className="page-hero bg-gradient-to-br from-secondary to-primary text-white">
        <div className="container-custom">
          <div className="max-w-3xl animate-fade-in-up">
            <nav className="flex items-center space-x-2 text-sm mb-4 text-gray-200">
              <Link to="/" className="hover:text-white">Accueil</Link>
              <span>/</span>
              <Link to="/services" className="hover:text-white">Services</Link>
              <span>/</span>
              <span className="text-white">Answering Service</span>
            </nav>
            <h1 className="font-heading text-4xl md:text-5xl font-bold mb-6">
              Answering Service – ZiExter
            </h1>
            <p className="text-xl text-gray-100 mb-4">
              Votre permanence téléphonique virtuelle 24h/24 et 7j/7
            </p>
            <p className="text-lg text-gray-200">
              Ne ratez plus jamais un appel client. Avec ZiExter, transformez chaque appel en opportunité grâce à notre service de réception d'appels professionnels, disponible 24/7.
            </p>
          </div>
        </div>
      </section>

      <section className="py-8 bg-accent">
        <div className="container-custom">
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <Link to="/devis" className="btn btn-secondary">Demandez un devis personnalisé</Link>
            <Link to="/contact" className="btn btn-outline-white">Contactez nos experts</Link>
          </div>
        </div>
      </section>

      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto animate-fade-in-up">
            <h2 className="section-title text-center mb-8">Vous êtes absent… mais vos clients appellent quand même</h2>
            <p className="text-lg text-gray-700 mb-4">
              Réunion importante, rendez-vous client, déjeuner, repos bien mérité… Et pendant ce temps, un appel crucial arrive. Sans réponse, c'est un client perdu, une opportunité manquée.
            </p>
            <p className="text-lg font-semibold text-primary">
              Avec ZiExter Answering Service, chaque appel reçoit une réponse professionnelle, même à 3h du matin. Nous devenons votre secrétariat téléphonique virtuel, disponible en permanence.
            </p>
          </div>
        </div>
      </section>

      <section className="section-padding bg-gray-50">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center mb-12 animate-fade-in-up">
            <h2 className="section-title">Ce que comprend notre Answering Service</h2>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            {[
              { title: 'Réception d\'appels 24/7', desc: 'Vos clients joignent toujours quelqu\'un, même la nuit, le week-end ou pendant les vacances.' },
              { title: 'Filtrage d\'appels', desc: 'Nous filtrons les appels selon vos instructions : urgences, prospects, fournisseurs, appels non sollicités.' },
              { title: 'Prise de messages détaillés', desc: 'Chaque appel est consigné avec soin : nom, coordonnées, motif, degré d\'urgence.' },
              { title: 'Transfert d\'appels intelligents', desc: 'Les appels urgents ou VIP vous sont transférés immédiatement selon vos critères.' },
              { title: 'Gestion d\'agenda simplifiée', desc: 'Nous prenons rendez-vous directement dans votre calendrier (Google Agenda, Outlook, etc.).' },
              { title: 'Accueil personnalisé', desc: 'Nos opérateurs répondent au nom de votre entreprise, avec vos scripts et vos messages d\'accueil.' }
            ].map((item, i) => (
              <div key={i} className="card hover-lift animate-fade-in-up" style={{ animationDelay: `${i * 50}ms` }}>
                <h3 className="font-bold mb-2">{item.title}</h3>
                <p className="text-sm text-gray-700">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center mb-12 animate-fade-in-up">
            <h2 className="section-title">Ils ont choisi ZiExter pour leur permanence téléphonique</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { title: 'Cabinet d\'avocats, Paris', problem: 'Appels manqués pendant les audiences.', solution: 'Mise en place d\'un answering service avec filtrage urgent/non-urgent.', result: '100% des appels clients traités, +25% de nouveaux dossiers.' },
              { title: 'PME plomberie, Lyon', problem: 'Urgences la nuit et le week-end non gérées.', solution: 'Permanence 24/7 avec transfert immédiat des urgences.', result: 'Satisfaction client en hausse, +30% de CA en interventions d\'urgence.' },
              { title: 'Consultant indépendant, Bruxelles', problem: 'Image peu professionnelle, pas de secrétaire.', solution: 'Answering service personnalisé avec prise de RDV.', result: 'Image renforcée, +40% de rendez-vous qualifiés.' }
            ].map((item, i) => (
              <div key={i} className="card hover-lift animate-fade-in-up" style={{ animationDelay: `${i * 100}ms` }}>
                <h3 className="font-bold mb-3">{item.title}</h3>
                <ul className="space-y-2 text-sm text-gray-700">
                  <li><strong>Problème :</strong> {item.problem}</li>
                  <li><strong>Solution ZiExter :</strong> {item.solution}</li>
                  <li><strong>Résultat :</strong> {item.result}</li>
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section-padding bg-gray-50">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center mb-12 animate-fade-in-up">
            <h2 className="section-title">Nos chiffres clés</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: '📞', stat: '5 000', label: 'appels traités chaque jour' },
              { icon: '⏱️', stat: '3 sonneries', label: 'Délai moyen de réponse' },
              { icon: '🌍', stat: '24/7', label: 'Disponibilité en 8 langues' },
              { icon: '⭐', stat: '98%', label: 'Taux de satisfaction client' }
            ].map((item, i) => (
              <div key={i} className="card text-center hover-lift animate-fade-in-up" style={{ animationDelay: `${i * 50}ms` }}>
                <div className="text-3xl mb-2">{item.icon}</div>
                <div className="text-2xl font-bold text-primary mb-1">{item.stat}</div>
                <p className="text-sm text-gray-600">{item.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center mb-12 animate-fade-in-up">
            <h2 className="section-title">Avis Clients</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { quote: 'Depuis que ZiExter gère mes appels, je peux me concentrer sur mes dossiers sans interruption. Le service est irréprochable.', name: 'Maître Sophie Durand', role: 'Avocate, Paris' },
              { quote: 'Nos clients apprécient d\'avoir toujours quelqu\'un au bout du fil, même à 22h. C\'est un vrai plus pour notre image.', name: 'Julien Lambert', role: 'Gérant, PME plomberie, Lyon' },
              { quote: 'Un service pro qui me fait gagner un temps fou. Je recommande vivement !', name: 'Marc Dufresne', role: 'Consultant indépendant, Bruxelles' }
            ].map((item, i) => (
              <div key={i} className="card hover-lift animate-fade-in-up" style={{ animationDelay: `${i * 100}ms` }}>
                <p className="text-gray-700 mb-4 italic">"{item.quote}"</p>
                <div className="font-semibold">{item.name}</div>
                <div className="text-sm text-gray-600">{item.role}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section-padding bg-gray-50">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center mb-12 animate-fade-in-up">
            <h2 className="section-title">FAQ</h2>
          </div>
          <div className="max-w-4xl mx-auto space-y-4">
            {[
              { q: 'Comment mes appels sont-ils transférés vers ZiExter ?', a: 'Vous redirigez simplement vos appels vers notre numéro dédié. Configuration en 5 minutes.' },
              { q: 'Puis-je personnaliser le message d\'accueil ?', a: 'Oui, nous créons un script sur mesure avec vos instructions et votre ton.' },
              { q: 'Recevez-vous aussi les appels en langue étrangère ?', a: 'Absolument. Nous gérons les appels en français, anglais, arabe, espagnol, etc.' },
              { q: 'Combien de temps gardez-vous les messages ?', a: 'Les messages sont conservés 30 jours minimum et vous sont envoyés par email/SMS immédiatement.' },
              { q: 'Puis-je tester le service avant de m\'engager ?', a: 'Oui, nous proposons une période d\'essai de 7 jours pour tester notre réactivité.' }
            ].map((item, i) => (
              <details key={i} className="card cursor-pointer">
                <summary className="font-semibold p-4 flex justify-between items-center">
                  <span>Q : {item.q}</span>
                  <svg className="w-5 h-5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </summary>
                <div className="p-4 pt-0 text-gray-600">{item.a}</div>
              </details>
            ))}
          </div>
        </div>
      </section>

      <section className="section-padding bg-gradient-to-r from-primary to-secondary text-white">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center animate-fade-in-up">
            <h2 className="font-heading text-3xl md:text-4xl font-bold mb-6">
              Offrez à vos clients une disponibilité totale
            </h2>
            <p className="text-xl text-gray-100 mb-8">
              ZiExter devient votre secrétariat virtuel : professionnel, disponible, et toujours prêt à répondre. Ne perdez plus jamais un client par manque de disponibilité.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact" className="btn btn-accent btn-lg">Demandez une démo gratuite</Link>
              <Link to="/devis" className="btn btn-outline-white btn-lg">Obtenez un devis personnalisé</Link>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default AnsweringService;
